import type { ReactNode } from 'react';
import { createContext, useEffect, useState, useCallback, useMemo, useLayoutEffect } from 'react';
import debounce from 'lodash/debounce';

export const BREAKPOINTS = {
  LG: 1024,
  XL: 1560,
  XXL: 1920,
  QS: 1280,
  MD: 800
};

export interface ScreenSizeState {
  isMobile: boolean;
  isXlDown: boolean;
  is2XlDown: boolean;
  isQsDown: boolean;
  isMdDown: boolean;
  screenSize: { width: number; height: number };
}

export interface ScreenSizeContextValue extends ScreenSizeState {
  isLgDown: boolean;
  isDesktop: boolean;
  isBot: boolean;
}

export const ScreenSizeContext = createContext<ScreenSizeContextValue | null>(null);

interface ScreenSizeProviderProps {
  children: ReactNode;
  initialIsMobile: boolean;
  isBot: boolean;
}

export function ScreenSizeProvider({ children, initialIsMobile, isBot }: Readonly<ScreenSizeProviderProps>) {
  const calculateBreakpoints = useCallback(
    (width: number) => ({
      isMobile: width < BREAKPOINTS.LG,
      isXlDown: width < BREAKPOINTS.XL,
      is2XlDown: width < BREAKPOINTS.XXL,
      isQsDown: width < BREAKPOINTS.QS,
      isMdDown: width < BREAKPOINTS.MD,
      screenSize: { width, height: typeof window !== 'undefined' ? window.innerHeight : 740 }
    }),
    []
  );

  const [state, setState] = useState<ScreenSizeState>(() => {
    const assumedWidth = initialIsMobile ? 360 : 1400;
    return calculateBreakpoints(assumedWidth);
  });

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const width = window.innerWidth;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState(calculateBreakpoints(width));
  }, [calculateBreakpoints]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const width = window.innerWidth;
      setState(calculateBreakpoints(width));
    };

    const debouncedHandleResize = debounce(handleResize, 100);
    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      debouncedHandleResize.cancel();
    };
  }, [calculateBreakpoints]);

  const value: ScreenSizeContextValue = useMemo(() => {
    return {
      ...state,
      isLgDown: state.isMobile,
      isDesktop: !state.isMobile,
      isBot
    };
  }, [state, isBot]);

  return <ScreenSizeContext.Provider value={value}>{children}</ScreenSizeContext.Provider>;
}
